import React, { useEffect, useLayoutEffect, Suspense, lazy } from 'react';
import '@/i18n';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@/context/ThemeContext';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, getLanguageFromPath } from '@/i18n';
import i18n from '@/i18n';

// Lazy-loaded Pages (Code Splitting)
const HomePage = lazy(() => import('@/pages/HomePage'));
const ConversionPage = lazy(() => import('@/pages/ConversionPage'));
const BatchPage = lazy(() => import('@/pages/BatchPage'));
const CompressPage = lazy(() => import('@/pages/CompressPage'));
const ComparePage = lazy(() => import('@/pages/ComparePage'));
const ViewerPage = lazy(() => import('@/pages/ViewerPage'));
const LearnPage = lazy(() => import('@/pages/LearnPage'));
const LearnArticlePage = lazy(() => import('@/pages/LearnArticlePage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogArticlePage = lazy(() => import('@/pages/BlogArticlePage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const ToolsPage = lazy(() => import('@/pages/ToolsPage'));
const SitemapPage = lazy(() => import('@/pages/SitemapPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const AccessibilityPage = lazy(() => import('@/pages/AccessibilityPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Components (eagerly loaded - small and always needed)
import CookieBanner from '@/components/CookieBanner';
import AccessibilityWidget from '@/components/AccessibilityWidget';

// Hooks
import useEzoic from '@/hooks/useEzoic';

// Ezoic Auto-ads for SPA navigation
const EzoicAutoAds = () => {
  useEzoic(); // No placeholder IDs - Ezoic handles placement dynamically
  return null;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Language sync component - uses useLayoutEffect for immediate language change before paint
const LanguageSync = ({ children }) => {
  const location = useLocation();
  const { i18n: i18nInstance } = useTranslation();

  // Set language immediately before any paint (for react-snap prerendering)
  useLayoutEffect(() => {
    const lang = getLanguageFromPath(location.pathname);
    if (i18nInstance.language !== lang) {
      i18nInstance.changeLanguage(lang);
    }
    document.documentElement.lang = lang;
  }, [location.pathname, i18nInstance]);

  return <>{children}</>;
};

// Create routes array for both with and without language prefix
const createRoutes = (prefix = '') => {
  const p = prefix ? `${prefix}/` : '';
  return (
    <>
      <Route path={`${p}`} element={<HomePage />} />
      <Route path={`${p}batch`} element={<BatchPage />} />
      <Route path={`${p}compress`} element={<CompressPage />} />
      <Route path={`${p}compare`} element={<ComparePage />} />
      <Route path={`${p}viewer`} element={<ViewerPage />} />
      <Route path={`${p}tools`} element={<ToolsPage />} />
      <Route path={`${p}faq`} element={<FAQPage />} />
      <Route path={`${p}sitemap`} element={<SitemapPage />} />
      
      <Route path={`${p}avif-to-jpg`} element={<ConversionPage />} />
      <Route path={`${p}avif-to-png`} element={<ConversionPage />} />
      <Route path={`${p}avif-to-webp`} element={<ConversionPage />} />
      <Route path={`${p}jpg-to-avif`} element={<ConversionPage />} />
      <Route path={`${p}png-to-avif`} element={<ConversionPage />} />
      <Route path={`${p}webp-to-avif`} element={<ConversionPage />} />
      <Route path={`${p}heic-to-avif`} element={<ConversionPage />} />
      
      <Route path={`${p}convert-avif`} element={<HomePage />} />
      <Route path={`${p}avif-converter-free`} element={<HomePage />} />
      <Route path={`${p}avif-image-converter`} element={<HomePage />} />
      <Route path={`${p}free-avif-converter`} element={<HomePage />} />
      <Route path={`${p}online-avif-converter`} element={<HomePage />} />
      
      <Route path={`${p}learn`} element={<LearnPage />} />
      <Route path={`${p}learn/:slug`} element={<LearnArticlePage />} />
      
      <Route path={`${p}blog`} element={<BlogPage />} />
      <Route path={`${p}blog/:slug`} element={<BlogArticlePage />} />
      
      <Route path={`${p}privacy`} element={<PrivacyPage />} />
      <Route path={`${p}cookie`} element={<CookiePolicyPage />} />
      <Route path={`${p}terms`} element={<TermsPage />} />
      <Route path={`${p}accessibility`} element={<AccessibilityPage />} />
      <Route path={`${p}contact`} element={<ContactPage />} />
      <Route path={`${p}about`} element={<AboutPage />} />
    </>
  );
};

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <EzoicAutoAds />
          <ScrollToTop />
          <LanguageSync>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* English routes (default, no prefix) */}
                {createRoutes('')}
                
                {/* English with /en/ prefix (redirect alias) */}
                {createRoutes('en')}
                
                {/* Italian */}
                {createRoutes('it')}
                
                {/* German */}
                {createRoutes('de')}
                
                {/* Spanish */}
                {createRoutes('es')}
                
                {/* French */}
                {createRoutes('fr')}
                
                {/* Portuguese */}
                {createRoutes('pt')}
                
                {/* Japanese */}
                {createRoutes('ja')}
                
                {/* Korean */}
                {createRoutes('ko')}
                
                {/* Chinese */}
                {createRoutes('zh')}
                
                {/* Russian */}
                {createRoutes('ru')}
                
                {/* 404 for any unmatched route */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>

            {/* Global Components */}
            <CookieBanner />
            <AccessibilityWidget />
          </LanguageSync>
          </BrowserRouter>
        </div>
      </ThemeProvider>
  );
}

export default App;
